import { Directive, Input, OnChanges, OnInit, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatDatepickerInput } from '@angular/material/datepicker';
import {
  collectEnabledDateKeys,
  cropYearNumber,
  formatLocalDate,
} from '../utils/crop-year-calendar';

/**
 * Restricts a Material datepicker to dates returned for the selected crop year.
 * The form value stays a yyyy-MM-dd string so report payloads do not shift a day.
 */
@Directive({
  selector: 'input[appCropYearDates]',
})
export class CropYearDatesDirective implements OnChanges, OnInit {
  @Input() cropYear: number | string | null = null;
  @Input() enabledDates: unknown[] | null = [];
  @Input() dateKey = 'key';

  private enabled = new Set<string>();
  private year: number | null = null;
  private readonly filter = (date: Date | null): boolean => {
    if (!date || !this.year) {
      return false;
    }
    return this.enabled.has(formatLocalDate(date));
  };

  constructor(
    private datepickerInput: MatDatepickerInput<Date>,
    @Optional() @Self() private ngControl: NgControl
  ) {}

  ngOnInit(): void {
    this.datepickerInput.dateFilter = this.filter;
    this.keepStringValue();
    this.applyBounds();
  }

  ngOnChanges(): void {
    this.enabled = collectEnabledDateKeys(this.enabledDates, this.dateKey);
    this.year = cropYearNumber(this.cropYear);
    this.applyBounds();
  }

  private applyBounds(): void {
    const min = this.year ? new Date(this.year, 0, 1) : null;
    const max = this.year ? new Date(this.year, 11, 31) : null;
    this.datepickerInput.min = min;
    this.datepickerInput.max = max;
    const picker = this.datepickerInput._datepicker as unknown as { startAt: Date | null } | undefined;
    if (picker && min) {
      picker.startAt = min;
    }
  }

  /** Datepicker writes a Date. Report APIs expect yyyy-MM-dd, and Date JSON becomes the previous UTC day. */
  private keepStringValue(): void {
    const control = this.ngControl?.control as any;
    if (!control || control.__cropYearDatesWrapped) {
      return;
    }
    const original = control.setValue.bind(control);
    control.setValue = (value: unknown, options?: object) => {
      if (value instanceof Date && !isNaN(value.getTime())) {
        value = formatLocalDate(value);
      }
      return original(value, options);
    };
    control.__cropYearDatesWrapped = true;
  }
}
